// Box Topper (MMP-316) helpers for the tab. The seal/template shapes and the
// placeholder list come from box-topper-print's own pure module, so the editor
// and the printer can never disagree about what a template is.
export {
  DEFAULT_SEAL_TEMPLATE,
  SEAL_PLACEHOLDERS,
  OFFER_TEXT_MAX,
  OFFER_TERMS_MAX,
  MAX_SEALS_PER_ORDER,
  TRACE_SEAL_INDEX,
  canRetry,
  previewValues,
  normalizeItemRule,
  sealCanvas,
  templateOrientation,
  type BoxTopperSettings,
  type ColorMode,
  type ItemRule,
  type SealElement,
  type SealMode,
  type SealOrientation,
  type SealQrElement,
  type SealRow,
  type SealStatus,
  type SealTemplate,
  type SealTextElement,
} from "../../supabase/functions/box-topper-print/lib.ts";

import {
  DEFAULT_SEAL_TEMPLATE,
  sealCanvas,
  templateOrientation,
  type SealQrElement,
  type SealRow,
  type SealTemplate,
  type SealTextElement,
} from "../../supabase/functions/box-topper-print/lib.ts";

/** The background image travels inside the template to the relay (8 MB body
 *  cap); keep it well under that so a save and every print stay fast. */
export const MAX_BACKGROUND_BYTES = 1_000_000;

/** The template without its background image (up to ~1.3 MB of base64), for
 *  comparisons that run on every keystroke; compare the image itself by
 *  reference, which is free when it has not changed. */
export const templateWithoutBackground = (template: SealTemplate): SealTemplate => ({ ...template, background_png_base64: undefined });

type Canvas = ReturnType<typeof sealCanvas>;

/** A new element starts near the top of the label, inside its width. */
export const newTextElement = (canvas: Canvas): SealTextElement => ({
  type: "text",
  text: "New text",
  x: canvas.orientation === "portrait" ? 40 : 90,
  y: 90,
  w: canvas.orientation === "portrait" ? canvas.width - 80 : 800,
  h: 120,
  font: "bold",
  font_size: 60,
  align: "left",
  valign: "middle",
  color: "black",
  wrap: true,
});

export const newQrElement = (canvas: Canvas): SealQrElement => ({
  type: "qr",
  x: canvas.orientation === "portrait" ? Math.round((canvas.width - 380) / 2) : 1230,
  y: 70,
  size: canvas.orientation === "portrait" ? 380 : 480,
  data: "{voucher_url}",
  ec_level: "M",
  quiet_zone: 2,
  color: "black",
});

/** The default vertical strip, keeping the owner's end-for-end flip (a fact
 *  about the printer, not the design) and, only when the current design is
 *  already a vertical strip, its background artwork: art made for the old
 *  landscape shape would print stretched. */
export const verticalLayoutFrom = (current: SealTemplate): SealTemplate => ({
  ...DEFAULT_SEAL_TEMPLATE,
  label: { ...DEFAULT_SEAL_TEMPLATE.label, rotate_180: current.label?.rotate_180 === true },
  background_png_base64: templateOrientation(current) === "portrait" ? (current.background_png_base64 ?? null) : null,
});

/** Plain-language problems that would make the relay refuse the template
 *  (its validate_template rules), checked before saving. */
export function templateProblems(template: SealTemplate): string[] {
  const problems: string[] = [];
  if (!template.elements.length) problems.push("Add at least one text or QR element.");
  template.elements.forEach((el, i) => {
    const name = `Element ${i + 1}`;
    const nums = el.type === "text" ? [el.x, el.y, el.w, el.h, el.font_size] : [el.x, el.y, el.size];
    if (nums.some((n) => typeof n !== "number" || !Number.isFinite(n))) {
      problems.push(`${name}: every position and size needs a number.`);
      return;
    }
    if (el.type === "text") {
      if (el.w <= 0 || el.h <= 0) problems.push(`${name}: width and height must be more than 0.`);
      if (el.font_size < 4 || el.font_size > 2000) problems.push(`${name}: font size must be between 4 and 2000.`);
    } else {
      if (el.size <= 0) problems.push(`${name}: QR size must be more than 0.`);
      if (!el.data.trim()) problems.push(`${name}: the QR needs something to encode, such as {voucher_url}.`);
    }
  });
  return problems;
}

/** Whether any part of an element falls off the printable label (portrait
 *  696 x 1800, landscape 1800 x 696: the template's own canvas). */
export function isOffLabel(el: SealTextElement | SealQrElement, canvas: Pick<Canvas, "width" | "height">): boolean {
  const w = el.type === "text" ? el.w : el.size;
  const h = el.type === "text" ? el.h : el.size;
  return el.x < 0 || el.y < 0 || el.x + w > canvas.width || el.y + h > canvas.height;
}

export interface SealOrderGroup {
  order_id: number;
  order_no: number | null;
  created_at: string;
  seals: SealRow[];
  failed: number;
}

/** Recent seals grouped per order, newest order first, seals in print order. */
export function groupSealsByOrder(seals: SealRow[]): SealOrderGroup[] {
  const groups = new Map<number, SealOrderGroup>();
  for (const seal of seals) {
    const group = groups.get(seal.order_id) ?? {
      order_id: seal.order_id,
      order_no: seal.order_no,
      created_at: seal.created_at,
      seals: [],
      failed: 0,
    };
    group.seals.push(seal);
    if (seal.status === "failed") group.failed += 1;
    if (seal.created_at > group.created_at) group.created_at = seal.created_at;
    groups.set(seal.order_id, group);
  }
  return [...groups.values()]
    .map((g) => ({ ...g, seals: g.seals.sort((a, b) => a.seal_index - b.seal_index) }))
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : b.order_id - a.order_id));
}

/** Reads a chosen image file as the bare base64 the relay wants (no data: prefix). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.includes(",") ? result.slice(result.indexOf(",") + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file"));
    reader.readAsDataURL(file);
  });
}
